import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ApiService } from "@app/core/services/api/api.service";
import { OutgoingMessage } from "@app/lists/lists-outgoing-messages/models/outgoing-messages";
import { OutgoingMail, PaginationListDTO } from "@app/models";
import { Receiver } from "@app/shared/modules/action-list/action-list-item/common";
import { API_DATE_FORMAT } from "@app/shared/utils/api-defaults";
import moment from "moment";
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";

type BatchStatus = "sent" | "upcoming";

interface OutgoingBatch extends OutgoingMail {
  messageType?: string;
}

interface ReceiverPreview {
  receiver: OutgoingMessage;
  content: string;
}

const RECEIVERS_PAGE_SIZE = 10;

@Component({
  selector: "app-outgoing-messages-widget",
  templateUrl: "./outgoing-messages-widget.component.html",
  styleUrls: ["./outgoing-messages-widget.component.scss"],
})
export class OutgoingMessagesWidgetComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() eaEmployeeId: string;

  @ViewChild("previewFrame") set previewFrame(frame: ElementRef) {
    if (frame && this.preview$.value) {
      this.writePreview(frame.nativeElement, this.preview$.value.content);
    }
  }

  statusFilters = [
    { label: "sent", value: "sent" },
    { label: "upcoming", value: "upcoming" },
  ];
  status: BatchStatus = "sent";

  batches$ = new BehaviorSubject<OutgoingBatch[]>([]);
  batchesLoading$ = new BehaviorSubject<boolean>(false);
  selectedBatch$ = new BehaviorSubject<OutgoingBatch>(null);
  receivers$ = new BehaviorSubject<OutgoingMessage[]>([]);
  receiversTotal$ = new BehaviorSubject<number>(0);
  receiversLoading$ = new BehaviorSubject<boolean>(false);
  preview$ = new BehaviorSubject<ReceiverPreview>(null);
  previewLoading$ = new BehaviorSubject<boolean>(false);

  private fetchBatches$ = new Subject<void>();
  private fetchReceivers$ = new Subject<number>();
  private previewReceiver$ = new Subject<OutgoingMessage>();
  private unsubscribe$ = new Subject<void>();

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.fetchBatches$
      .pipe(
        tap(() => this.batchesLoading$.next(true)),
        switchMap(() =>
          this.loadBatches().pipe(
            tap((batches) => {
              this.batches$.next(batches);
              this.batchesLoading$.next(false);
            }),
            switchMap((batches) => this.loadMessageTypes(batches))
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((batches) => this.batches$.next(batches));

    this.fetchReceivers$
      .pipe(
        tap(() => this.receiversLoading$.next(true)),
        switchMap((offset) => this.loadReceivers(offset)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(({ offset, response }) => {
        const loaded = offset === 0 ? [] : this.receivers$.value;
        this.receivers$.next([...loaded, ...(response?.rows ?? [])]);
        if (response || offset === 0) {
          this.receiversTotal$.next(response?.total ?? 0);
        }
        this.receiversLoading$.next(false);
      });

    this.previewReceiver$
      .pipe(
        tap((receiver) => this.previewLoading$.next(!!receiver)),
        switchMap((receiver) =>
          receiver ? this.loadPreview(receiver) : of(null)
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((preview) => {
        this.previewLoading$.next(false);
        this.preview$.next(preview);
      });

    this.fetchBatches$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.eaEmployeeId && !changes.eaEmployeeId.firstChange) {
      this.closeBatch();
      this.fetchBatches$.next();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  changeStatus(status: BatchStatus): void {
    this.status = status;
    this.batches$.next([]);
    this.fetchBatches$.next();
  }

  openBatch(batch: OutgoingBatch): void {
    this.selectedBatch$.next(batch);
    this.receivers$.next([]);
    this.receiversTotal$.next(0);
    this.fetchReceivers$.next(0);
  }

  closeBatch(): void {
    this.closePreview();
    this.selectedBatch$.next(null);
    this.receivers$.next([]);
  }

  loadMoreReceivers(): void {
    this.fetchReceivers$.next(this.receivers$.value.length);
  }

  openPreview(receiver: OutgoingMessage): void {
    this.previewReceiver$.next(receiver);
  }

  closePreview(): void {
    this.previewReceiver$.next(null);
  }

  getIcon(batch: OutgoingBatch): string {
    return batch.period === "none" ? "envelope" : "repeat";
  }

  getTimeFromNow(batch: OutgoingBatch): string {
    return moment(batch.sendTime, "YYYY-MM-DD HH:mm").fromNow();
  }

  private loadBatches(): Observable<OutgoingBatch[]> {
    const params = {
      status: this.status === "sent" ? "sent" : "pregenerated",
      ...(this.eaEmployeeId ? { eaEmployeeId: this.eaEmployeeId } : {}),
    };

    return this.apiService.get("bulk-messages", params).pipe(
      map((response: PaginationListDTO) =>
        this.sortBatches(response?.rows ?? [])
      ),
      catchError(() => of([]))
    );
  }

  private sortBatches(batches: OutgoingBatch[]): OutgoingBatch[] {
    const direction = this.status === "sent" ? -1 : 1;
    return [...batches].sort(
      (a, b) => direction * (a.sendTime ?? "").localeCompare(b.sendTime ?? "")
    );
  }

  private loadMessageTypes(
    batches: OutgoingBatch[]
  ): Observable<OutgoingBatch[]> {
    if (batches.length === 0) {
      return of(batches);
    }

    return forkJoin(
      batches.map((batch) =>
        this.apiService
          .get("ea-crm-receivers/search", {
            ...this.getReceiverParams(batch),
            limit: 1,
          })
          .pipe(
            map((response: PaginationListDTO) => ({
              ...batch,
              messageType: response?.rows?.[0]?.messageType,
            })),
            catchError(() => of(batch))
          )
      )
    );
  }

  private loadReceivers(offset: number) {
    const params = {
      ...this.getReceiverParams(this.selectedBatch$.value),
      sortBy: "firstName",
      sortOrder: "asc",
      limit: RECEIVERS_PAGE_SIZE,
      offset,
    };

    return this.apiService.get("ea-crm-receivers/search", params).pipe(
      map((response: PaginationListDTO) => ({ offset, response })),
      catchError(() => of({ offset, response: null }))
    );
  }

  private loadPreview(receiver: OutgoingMessage): Observable<ReceiverPreview> {
    return this.apiService
      .get(`ea-crm-receivers/${receiver.receiverId}`, {})
      .pipe(
        map((r: Receiver) => ({ receiver, content: r.content })),
        catchError(() => of({ receiver, content: "" }))
      );
  }

  private getReceiverParams(batch: OutgoingBatch) {
    const sendTime = moment(batch.sendTime, "YYYY-MM-DD HH:mm");
    return {
      activityId: batch.eaCrmActivityId,
      batchSendTimeMin: sendTime.format(API_DATE_FORMAT),
      batchSendTimeMax: sendTime.endOf("minute").format(API_DATE_FORMAT),
      status: this.status === "sent" ? "sent" : "spooled",
    };
  }

  private writePreview(frame: HTMLIFrameElement, html: string): void {
    const doc = frame.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();
    doc.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });

    const overflow = doc.documentElement.scrollWidth / frame.clientWidth;
    if (frame.clientWidth > 0 && overflow > 1) {
      doc.body?.style.setProperty("zoom", `${1 / overflow}`);
    }
  }
}
